import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../utils/api';
import { getTeamLogoUrl } from '../../utils/logoUrl';

interface Competition {
  id: string;
  name: string;
  country: string;
  tier: number;
}

interface Team {
  id: string;
  name: string;
  shortName?: string;
  countryCode?: string;
  logoUrl?: string;
  competitionId?: string;
  competition?: Competition;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const AdminTeams: React.FC = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompetition, setSelectedCompetition] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    shortName: '',
    countryCode: '',
    competitionId: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    fetchTeams();
    fetchCompetitions();
  }, []);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/teams');
      setTeams(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch teams');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompetitions = async () => {
    try {
      const response = await apiClient.get('/admin/competitions');
      setCompetitions(response.data);
    } catch (err: any) {
      console.error('Failed to fetch competitions:', err);
    }
  };

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('Logo file size must be less than 5MB');
        return;
      }
      
      if (!['image/jpeg', 'image/jpg', 'image/png', 'image/gif'].includes(file.type)) {
        setError('Logo must be a JPEG, PNG, or GIF image');
        return;
      }

      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const uploadLogo = async (teamId: string) => {
    if (!logoFile) return null;

    setUploadingLogo(true);
    const logoFormData = new FormData();
    logoFormData.append('logo', logoFile);

    try {
      const response = await apiClient.post(`/admin/teams/${teamId}/logo`, logoFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.logoUrl;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload logo');
      return null;
    } finally {
      setUploadingLogo(false);
    }
  };

  const deleteLogo = async (teamId: string) => {
    try {
      await apiClient.delete(`/admin/teams/${teamId}/logo`);
      fetchTeams(); // Refresh the teams list
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete logo');
    }
  };

  const createTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // First create the team
      const response = await apiClient.post('/admin/teams', formData);
      const teamId = response.data.id;
      
      // Then upload logo if provided
      if (logoFile) {
        await uploadLogo(teamId);
      }
      
      setShowCreateModal(false);
      resetForm();
      fetchTeams();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create team');
    } finally {
      setSubmitting(false);
    }
  };

  const updateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTeam) return;
    
    setSubmitting(true);
    try {
      // First update the team
      await apiClient.put(`/admin/teams/${editingTeam.id}`, formData);
      
      // Then upload new logo if provided
      if (logoFile) {
        await uploadLogo(editingTeam.id);
      }
      
      setEditingTeam(null);
      setShowCreateModal(false);
      resetForm();
      fetchTeams();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update team');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteTeam = async (teamId: string) => {
    if (!window.confirm('Are you sure you want to delete this team? This will also delete all related matches and predictions.')) {
      return;
    }

    try {
      await apiClient.delete(`/admin/teams/${teamId}`);
      fetchTeams();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete team');
    }
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (team: Team) => {
    setEditingTeam(team);
    setFormData({
      name: team.name,
      shortName: team.shortName || '',
      countryCode: team.countryCode || '',
      competitionId: team.competitionId || ''
    });
    // Set existing logo as preview if available
    if (team.logoUrl) {
      setLogoPreview(getTeamLogoUrl(team.logoUrl));
    }
    setShowCreateModal(true);
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingTeam(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      shortName: '',
      countryCode: '',
      competitionId: ''
    });
    setLogoFile(null);
    setLogoPreview(null);
    setError('');
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCompetition('');
    setSelectedCountry('');
  };

  // Get unique countries from teams for filter
  const uniqueCountries = Array.from(new Set(teams.map(team => team.countryCode).filter(Boolean))).sort();

  // Filter teams based on search and filters
  const filteredTeams = teams.filter(team => {
    const matchesSearch = team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (team.shortName && team.shortName.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCompetition = !selectedCompetition || team.competition?.id === selectedCompetition;
    const matchesCountry = !selectedCountry || team.countryCode === selectedCountry;

    return matchesSearch && matchesCompetition && matchesCountry;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading teams...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Manage Teams
          </h1>
          <Link 
            to="/admin" 
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            ← Back to Admin Dashboard
          </Link>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={openCreateModal}
            className="btn-primary px-4 py-2"
          >
            Add New Team
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
              Search Teams
            </label>
            <input
              type="text"
              placeholder="Search by name or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
              Competition
            </label>
            <select
              value={selectedCompetition}
              onChange={(e) => setSelectedCompetition(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
            >
              <option value="">All Competitions</option>
              {competitions.map((competition) => (
                <option key={competition.id} value={competition.id}>
                  {competition.name} ({competition.country})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
              Country
            </label>
            <select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
            >
              <option value="">All Countries</option>
              {uniqueCountries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="btn-secondary px-4 py-2 w-full"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-4">
        {filteredTeams.map((team) => (
          <div key={team.id} className="card p-6">
            <div className="flex justify-between items-start">
              <div className="flex items-start gap-4 flex-1">
                {team.logoUrl && (
                  <img 
                    src={getTeamLogoUrl(team.logoUrl)}
                    alt={`${team.name} logo`}
                    className="w-12 h-12 object-contain rounded border"
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {team.name}
                    </h3>
                    {team.shortName && (
                      <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                        {team.shortName}
                      </span>
                    )}
                  </div>
                  
                  <div className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                    {team.countryCode && <span className="mr-4">🌍 {team.countryCode}</span>}
                    {team.competition && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                        🏆 {team.competition.name}
                      </span>
                    )}
                    {!team.competition && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200">
                        ⚠️ No Competition
                      </span>
                    )}
                  </div>

                  <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                    Created: {new Date(team.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => openEditModal(team)}
                  className="btn-secondary px-3 py-1 text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => deleteTeam(team.id)}
                  className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}

        {filteredTeams.length === 0 && (
          <div className="card p-8 text-center">
            <p className="text-xl text-gray-500">
              {searchTerm || selectedCompetition || selectedCountry ? 
                'No teams match your filters.' : 
                'No teams found. Add your first team!'
              }
            </p>
            {(searchTerm || selectedCompetition || selectedCountry) && (
              <button
                onClick={clearFilters}
                className="btn-secondary mt-4"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              {editingTeam ? 'Edit Team' : 'Add New Team'}
            </h2>
            
            <form onSubmit={editingTeam ? updateTeam : createTeam}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                  Team Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                  required
                  placeholder="e.g., Manchester United"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                  Short Name
                </label>
                <input
                  type="text"
                  value={formData.shortName}
                  onChange={(e) => setFormData({ ...formData, shortName: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                  maxLength={10}
                  placeholder="e.g., MAN UTD"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                  Country Code
                </label>
                <input
                  type="text"
                  value={formData.countryCode}
                  onChange={(e) => setFormData({ ...formData, countryCode: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                  maxLength={3}
                  placeholder="e.g., ENG"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                  Competition
                </label>
                <select
                  value={formData.competitionId}
                  onChange={(e) => setFormData({ ...formData, competitionId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                >
                  <option value="">Select a competition...</option>
                  {competitions.map((competition) => (
                    <option key={competition.id} value={competition.id}>
                      {competition.name} ({competition.country})
                    </option>
                  ))}
                </select>
              </div>



              <div className="mb-6">
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                  Team Logo
                </label>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif"
                  onChange={handleLogoSelect}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                />
                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                  PNG, JPG, or GIF. Max 5MB.
                </p>
                
                {logoPreview && (
                  <div className="mt-3">
                    <img 
                      src={logoPreview} 
                      alt="Logo preview" 
                      className="w-16 h-16 object-contain rounded border"
                    />
                    {editingTeam && editingTeam.logoUrl && (
                      <button
                        type="button"
                        onClick={() => editingTeam && deleteLogo(editingTeam.id)}
                        className="ml-3 text-red-600 text-sm hover:text-red-800"
                      >
                        Delete Current Logo
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  className="btn-primary flex-1"
                  disabled={submitting || uploadingLogo}
                >
                  {submitting || uploadingLogo ? 'Saving...' : editingTeam ? 'Update Team' : 'Create Team'}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-secondary flex-1"
                  disabled={submitting || uploadingLogo}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTeams; 